import { ChevronRight, Play, X } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Eyebrow } from "~/components/ui/eyebrow";
import { PartTypeBadge } from "~/components/ui/part-type-badge";
import type { MeditationVoiceOption } from "~/features/meditations/hooks/use-meditation-voice-options";
import type { MeditationVoiceContext } from "~/features/meditations/hooks/use-meditation-voice-options";
import { rememberMeditationVoice } from "~/features/meditations/hooks/use-meditation-voice-options";
import { useMeditationSetupOptions } from "~/features/meditations/hooks/use-meditation-setup-options";
import type {
    MeditationCatalogId,
    MeditationOpening,
    MeditationPauseMultiplier,
    MeditationSetupMode,
    MeditationSetupOption,
    MeditationSetupSelection,
    SelectableVoice,
} from "~/features/meditations/types";
import { estimateMeditationMinutes } from "~/features/meditations/utils/meditation-catalog";
import { formatApproximateLength } from "~/features/meditations/utils/meditation-length";
import { getPartTypeFamily } from "~/features/parts-similarity/utils/type-family";
import { cn } from "~/lib/utils";

import { MeditationPickerScreen } from "./meditation-picker-screen";
import { OpeningPickerScreen, openingLabel } from "./opening-picker-screen";
import { PausePickerScreen, pauseMultiplierLabel } from "./pause-picker-screen";
import { SetupRow } from "./setup-row";
import { VoicePickerScreen } from "./voice-picker-screen";

const PAUSE_STORAGE_KEY = "meditation-pause-multiplier";
const OPENING_STORAGE_KEY = "meditation-opening";

type SetupScreen = "setup" | "meditation" | "voice" | "pauses" | "opening";
type TransitionDirection = "forward" | "back";

export type { MeditationSetupSelection } from "~/features/meditations/types";

interface MeditationSetupOverlayProps {
    mode: MeditationSetupMode;
    /** The part's own image, shown as a circle beside the title. Omitted when images are off. */
    partImageUrl?: string;
    // AIDEV-NOTE: EVO-2476 — a catalog meditation's length comes off `selectedMeditation`
    // (spokenMin / pauseMin), which exists only in part mode. A resource exercise has no such
    // record, so its host derives the length from the verbatim script and hands it down here.
    // Returns null when nothing knows the length (a generative template before it is rendered),
    // and the length is then omitted rather than guessed at.
    estimatedSecondsFor?: (
        pauseMultiplier: MeditationPauseMultiplier,
    ) => number | null;
    voiceOptions: MeditationVoiceOption[];
    initialVoice: SelectableVoice;
    defaultVoice: SelectableVoice;
    voiceContext?: MeditationVoiceContext;
    onClose: () => void;
    onStart: (selection: MeditationSetupSelection) => void;
}

function readPauseMultiplier(): MeditationPauseMultiplier {
    if (typeof window === "undefined") return 1;
    try {
        const stored = window.localStorage.getItem(PAUSE_STORAGE_KEY);
        return stored === "2" ? 2 : stored === "3" ? 3 : 1;
    } catch {
        return 1;
    }
}

function readOpening(): MeditationOpening {
    if (typeof window === "undefined") return "full";
    try {
        return window.localStorage.getItem(OPENING_STORAGE_KEY) === "brief"
            ? "brief"
            : "full";
    } catch {
        return "full";
    }
}

function rememberOpening(opening: MeditationOpening): void {
    try {
        window.localStorage.setItem(OPENING_STORAGE_KEY, opening);
    } catch {
        // Storage can be unavailable in private mode; the in-memory choice still works.
    }
}

function rememberPauseMultiplier(multiplier: MeditationPauseMultiplier): void {
    try {
        window.localStorage.setItem(PAUSE_STORAGE_KEY, String(multiplier));
    } catch {
        // Storage can be unavailable in private mode; the in-memory choice still works.
    }
}

export function MeditationSetupOverlay({
    mode,
    partImageUrl,
    estimatedSecondsFor,
    voiceOptions,
    initialVoice,
    defaultVoice,
    voiceContext: presetVoiceContext = "general",
    onClose,
    onStart,
}: MeditationSetupOverlayProps) {
    const isPartMode = mode.kind === "part";
    // AIDEV-NOTE: Presets are fully specified by their host and must never fetch the part catalog.
    const setupQuery = useMeditationSetupOptions(
        isPartMode ? mode.partInteractionId : "",
        { enabled: isPartMode },
    );
    const [screen, setScreen] = useState<SetupScreen>("setup");
    const [direction, setDirection] = useState<TransitionDirection>("forward");
    const [selectedMeditationId, setSelectedMeditationId] =
        useState<MeditationCatalogId | null>(null);
    const [selectedVoice, setSelectedVoice] =
        useState<SelectableVoice>(initialVoice);
    const [voiceTouched, setVoiceTouched] = useState(false);
    const [pauseMultiplier, setPauseMultiplier] =
        useState<MeditationPauseMultiplier>(readPauseMultiplier);
    const [opening, setOpening] = useState<MeditationOpening>(readOpening);

    const data = isPartMode ? setupQuery.data : undefined;
    const selectedMeditation = useMemo(
        () =>
            data?.options.find(
                (option) => option.id === selectedMeditationId,
            ) ?? data?.options.find((option) => option.suggested),
        [data, selectedMeditationId],
    );

    useEffect(() => {
        if (!data || selectedMeditationId) return;
        setSelectedMeditationId(
            data.options.find((option) => option.suggested)?.id ??
                data.options[0]?.id ??
                null,
        );
    }, [data, selectedMeditationId]);

    useEffect(() => {
        if (voiceOptions.some((option) => option.value === selectedVoice)) {
            return;
        }
        setSelectedVoice(defaultVoice);
    }, [defaultVoice, selectedVoice, voiceOptions]);

    useEffect(() => {
        if (!isPartMode || !selectedMeditation || voiceTouched) return;
        const michelle = voiceOptions.find(
            (option) =>
                option.kind === "catalog" && option.value === "Michelle",
        );
        setSelectedVoice(
            selectedMeditation.isDpmp && michelle
                ? michelle.value
                : initialVoice,
        );
    }, [
        initialVoice,
        isPartMode,
        selectedMeditation,
        voiceOptions,
        voiceTouched,
    ]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    const openScreen = useCallback((next: SetupScreen) => {
        setDirection("forward");
        setScreen(next);
    }, []);
    const returnToSetup = useCallback(() => {
        setDirection("back");
        setScreen("setup");
    }, []);
    const selectMeditation = useCallback(
        (option: MeditationSetupOption) => {
            setSelectedMeditationId(option.id);
            if (!voiceTouched) {
                const michelle = voiceOptions.find(
                    (voice) =>
                        voice.kind === "catalog" && voice.value === "Michelle",
                );
                setSelectedVoice(
                    option.isDpmp && michelle ? michelle.value : initialVoice,
                );
            }
            returnToSetup();
        },
        [initialVoice, returnToSetup, voiceOptions, voiceTouched],
    );
    // AIDEV-NOTE: Voice memory is context-scoped (general vs dpmp) so a DPMP pick does not
    // leak into ordinary meditations and vice versa — see use-meditation-voice-options.
    const voiceContext: MeditationVoiceContext = isPartMode
        ? selectedMeditation?.isDpmp
            ? "dpmp"
            : "general"
        : mode.kind === "experience"
          ? presetVoiceContext
          : "general";
    const selectVoice = useCallback(
        (voice: SelectableVoice) => {
            setVoiceTouched(true);
            setSelectedVoice(voice);
            rememberMeditationVoice(voice, voiceContext);
            returnToSetup();
        },
        [returnToSetup, voiceContext],
    );
    const selectOpening = useCallback(
        (next: MeditationOpening) => {
            setOpening(next);
            rememberOpening(next);
            returnToSetup();
        },
        [returnToSetup],
    );
    const selectPauses = useCallback(
        (multiplier: MeditationPauseMultiplier) => {
            setPauseMultiplier(multiplier);
            rememberPauseMultiplier(multiplier);
            returnToSetup();
        },
        [returnToSetup],
    );
    const handleStart = useCallback(() => {
        rememberMeditationVoice(selectedVoice, voiceContext);
        rememberPauseMultiplier(pauseMultiplier);
        if (isPartMode) {
            if (!selectedMeditation || !data) return;
            rememberOpening(opening);
            onStart({
                kind: "part",
                part: data.part,
                option: selectedMeditation,
                voice: selectedVoice,
                pauseMultiplier,
                // Only the generated check-in reads this; resource scripts ignore it.
                opening:
                    selectedMeditation.backing === "generated"
                        ? opening
                        : "full",
            });
            return;
        }
        onStart({
            kind: "preset",
            preset: mode,
            voice: selectedVoice,
            pauseMultiplier,
        });
    }, [
        data,
        isPartMode,
        mode,
        onStart,
        opening,
        pauseMultiplier,
        selectedMeditation,
        selectedVoice,
        voiceContext,
    ]);

    // AIDEV-NOTE: EVO-2476 — ONE length resolver for the whole overlay, so the setup row and
    // the pacing cards cannot disagree about how long a practice runs. Part mode reads the
    // catalog entry the user picked; a resource exercise asks its host. Both already account
    // for the pause multiplier, which is why this takes one: at 3x pacing a 20-minute practice
    // is closer to 35, and "give yourself the right amount of time" is the whole point of the
    // number being on screen.
    const estimatedSecondsAt = useCallback(
        (multiplier: MeditationPauseMultiplier): number | null => {
            if (isPartMode) {
                return selectedMeditation
                    ? estimateMeditationMinutes(
                          selectedMeditation,
                          multiplier,
                      ) * 60
                    : null;
            }
            return estimatedSecondsFor?.(multiplier) ?? null;
        },
        [estimatedSecondsFor, isPartMode, selectedMeditation],
    );
    const estimatedLengthLabel = formatApproximateLength(
        estimatedSecondsAt(pauseMultiplier),
    );

    const displayedPartName =
        data?.part.partName ?? (mode.kind === "part" ? mode.partName : "");
    const transitionClass = cn(
        // sm:overflow-y-auto lets the vertically centred setup block scroll on short viewports.
        "flex min-h-0 flex-1 flex-col sm:overflow-y-auto",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200",
        direction === "forward"
            ? "motion-safe:slide-in-from-right-3"
            : "motion-safe:slide-in-from-left-3",
    );

    let content: ReactNode;
    if (isPartMode && screen === "meditation" && data && selectedMeditation) {
        content = (
            <MeditationPickerScreen
                options={data.options}
                selectedId={selectedMeditation.id}
                family={getPartTypeFamily(data.part.partType)}
                partType={data.part.partType}
                pauseMultiplier={pauseMultiplier}
                onSelect={selectMeditation}
                onBack={returnToSetup}
            />
        );
    } else if (screen === "voice") {
        content = (
            <VoicePickerScreen
                options={voiceOptions}
                selectedVoice={selectedVoice}
                onSelect={selectVoice}
                onBack={returnToSetup}
            />
        );
    } else if (screen === "opening") {
        content = (
            <OpeningPickerScreen
                selectedOpening={opening}
                onSelect={selectOpening}
                onBack={returnToSetup}
            />
        );
    } else if (screen === "pauses") {
        content = (
            <PausePickerScreen
                selectedMultiplier={pauseMultiplier}
                estimatedSecondsAt={estimatedSecondsAt}
                practiceName={
                    isPartMode ? selectedMeditation?.title : mode.title
                }
                onSelect={selectPauses}
                onBack={returnToSetup}
            />
        );
    } else {
        const loading = isPartMode && setupQuery.isLoading;
        const selectedImageUrl = selectedMeditation?.imageUrl ?? null;
        const eyebrow =
            mode.kind === "part"
                ? "Connect with"
                : mode.kind === "type"
                  ? "Session meditation"
                  : mode.resourceTitle;
        const title = mode.kind === "part" ? displayedPartName : mode.title;
        const showOpeningRow =
            isPartMode && selectedMeditation?.backing === "generated";
        // AIDEV-NOTE: EVO-2476 — the length rides with the meditation's NAME rather than
        // getting a row of its own: it is an attribute of the practice, and a fourth row would
        // push Start off a phone screen (see the note on the row grid below).
        const lengthNote = estimatedLengthLabel ? (
            <span className="font-body text-sm font-normal text-stone-500">
                {estimatedLengthLabel}
            </span>
        ) : null;
        content = (
            // AIDEV-NOTE: Mobile pins Start to the bottom (main scrolls, CTA fixed). From `sm`
            // up the header, form and Start centre vertically as ONE group (Brent, 2026-09-09):
            // main stops growing and the whole block floats with my-auto inside the scrolling
            // column, so a short viewport still scrolls instead of clipping.
            <div className="flex min-h-0 flex-1 flex-col sm:my-auto sm:flex-none">
                <main className="flex-1 overflow-y-auto px-5 pb-6 pt-16 sm:flex-none sm:overflow-visible sm:px-6 sm:pt-6">
                    {/* AIDEV-NOTE: The part's face sits beside its name, not above the fold
                        on its own — a circle, so it reads as the part rather than as another
                        illustration competing with the meditation art below (Adam,
                        2026-09-09). Omitted entirely when the part has no image or images
                        are off, and the text simply takes the full width. */}
                    <header className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <Eyebrow as="p" tone="muted">
                                {eyebrow}
                            </Eyebrow>
                            {/* display-h1 per DESIGN.md (Playfair 32/40/700, -0.01em). */}
                            <h1
                                id="meditation-setup-title"
                                className="mt-1 font-display text-3xl font-bold leading-[2rem] tracking-[-0.01em] text-brand-forest-800"
                            >
                                {title}
                            </h1>
                            {isPartMode ? (
                                <div className="mt-3 min-h-5">
                                    {data ? (
                                        <PartTypeBadge
                                            type={data.part.partType}
                                            appearance="ghost"
                                        />
                                    ) : (
                                        <span className="block h-4 w-24 animate-pulse rounded bg-stone-200 motion-reduce:animate-none" />
                                    )}
                                </div>
                            ) : null}
                        </div>
                        {partImageUrl ? (
                            <img
                                src={partImageUrl}
                                alt=""
                                aria-hidden="true"
                                className="size-16 shrink-0 rounded-full object-cover ring-1 ring-brand-forest-100 sm:size-20"
                            />
                        ) : null}
                    </header>

                    {isPartMode && setupQuery.isError ? (
                        <div className="mt-8 rounded-2xl border border-brand-forest-100 bg-white p-5 shadow-card">
                            <p className="font-body text-base leading-relaxed text-stone-700">
                                We couldn&apos;t load the meditation choices.
                            </p>
                            <Button
                                variant="neutral"
                                onClick={() => setupQuery.refetch()}
                                className="mt-4"
                            >
                                Retry loading choices
                            </Button>
                        </div>
                    ) : (
                        <div className="mt-8 overflow-hidden rounded-2xl border border-brand-forest-100 bg-white shadow-card">
                            {/* AIDEV-NOTE: The chosen meditation's watercolor, so the setup
                                screen shows WHICH meditation is queued rather than only naming
                                it. Hidden below `sm`: on a phone the three rows plus the CTA
                                already fill the viewport, and a banner pushed Start off-screen. */}
                            {/* AIDEV-NOTE: The meditation choice lives ON its own artwork
                                (Adam, 2026-09-09) — the picture and the thing it depicts are
                                one control, rather than a decorative banner with its label
                                parked underneath. A bottom-up scrim plus white text keeps the
                                copy readable over ANY of the watercolours, which run from
                                pale cream to deep forest; the title clamps at two lines so a
                                long name never pushes the Change affordance out of the frame.
                                Falls back to a plain row when the meditation has no image. */}
                            {isPartMode && selectedImageUrl ? (
                                <button
                                    type="button"
                                    onClick={() => openScreen("meditation")}
                                    disabled={!selectedMeditation || loading}
                                    data-testid="meditation-setup-row-meditation"
                                    className="group relative block w-full overflow-hidden text-left disabled:cursor-default"
                                >
                                    <span className="block aspect-[16/7] w-full bg-brand-cream-100 sm:aspect-[16/6]">
                                        <img
                                            src={selectedImageUrl}
                                            alt=""
                                            aria-hidden="true"
                                            className="size-full object-cover"
                                        />
                                    </span>
                                    <span
                                        aria-hidden="true"
                                        className="absolute inset-0 bg-gradient-to-t from-brand-forest-900/90 via-brand-forest-900/60 to-brand-forest-900/10"
                                    />
                                    <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 sm:p-5">
                                        <span className="min-w-0">
                                            {/* AIDEV-NOTE: ONE badge, whose label carries
                                                the suggestion rather than a second pill
                                                beneath the title (Adam, 2026-09-09).
                                                A Badge and not an Eyebrow: u-eyebrow sets
                                                its own colour, which beat the override and
                                                rendered forest-on-forest, and a white/20
                                                pill went muddy over the watercolours.
                                                Badge variants are opaque, so they hold on
                                                every image in the set. */}
                                            <Badge
                                                variant={
                                                    selectedMeditation?.suggested
                                                        ? "terra"
                                                        : "forest"
                                                }
                                            >
                                                {selectedMeditation?.suggested
                                                    ? "Suggested Meditation"
                                                    : "Meditation"}
                                            </Badge>
                                            {loading ? (
                                                <span className="mt-2 block h-6 w-40 animate-pulse rounded bg-white/30 motion-reduce:animate-none" />
                                            ) : (
                                                <>
                                                    <span className="mt-2 block font-display text-xl font-semibold leading-tight tracking-[-0.01em] text-white [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">
                                                        {
                                                            selectedMeditation?.title
                                                        }
                                                    </span>
                                                    {/* AIDEV-NOTE: EVO-2476 — body-sm on the
                                                        scrim, not a badge: it is a fact about
                                                        the practice, not a label competing with
                                                        the one above the title. */}
                                                    {estimatedLengthLabel ? (
                                                        <span className="mt-1 block font-body text-sm text-white/80">
                                                            {
                                                                estimatedLengthLabel
                                                            }
                                                        </span>
                                                    ) : null}
                                                </>
                                            )}
                                        </span>
                                        <span className="flex shrink-0 items-center gap-1 font-body text-sm font-medium text-white">
                                            Change
                                            <ChevronRight className="size-4" />
                                        </span>
                                    </span>
                                </button>
                            ) : (
                                <SetupRow
                                    label="Meditation"
                                    loading={loading}
                                    disabled={isPartMode && !selectedMeditation}
                                    onClick={
                                        isPartMode
                                            ? () => openScreen("meditation")
                                            : undefined
                                    }
                                    interactive={isPartMode}
                                    testId={
                                        isPartMode
                                            ? "meditation-setup-row-meditation"
                                            : "meditation-setup-preset"
                                    }
                                    showDivider={false}
                                    value={
                                        isPartMode && selectedMeditation ? (
                                            <>
                                                {selectedMeditation.title}
                                                {selectedMeditation.suggested ? (
                                                    <Badge variant="terra">
                                                        Suggested
                                                    </Badge>
                                                ) : null}
                                                {lengthNote}
                                            </>
                                        ) : mode.kind !== "part" ? (
                                            <>
                                                {mode.title}
                                                {lengthNote}
                                            </>
                                        ) : undefined
                                    }
                                />
                            )}
                            {/* Voice, pacing, and optional opening stack on mobile. */}
                            <div
                                className={cn(
                                    "border-t border-brand-forest-100 sm:grid",
                                    showOpeningRow
                                        ? "sm:grid-cols-3"
                                        : "sm:grid-cols-2",
                                )}
                            >
                                <SetupRow
                                    label="Voice"
                                    loading={loading}
                                    disabled={false}
                                    onClick={() => openScreen("voice")}
                                    testId="meditation-setup-row-voice"
                                    value={
                                        voiceOptions.find(
                                            (voice) =>
                                                voice.value === selectedVoice,
                                        )?.label ?? "Voice"
                                    }
                                />
                                <SetupRow
                                    label="Pacing"
                                    loading={loading}
                                    disabled={false}
                                    onClick={() => openScreen("pauses")}
                                    testId="meditation-setup-row-pauses"
                                    showDivider={!showOpeningRow}
                                    value={pauseMultiplierLabel(
                                        pauseMultiplier,
                                    )}
                                />
                                {showOpeningRow ? (
                                    <SetupRow
                                        label="Opening"
                                        loading={loading}
                                        disabled={false}
                                        onClick={() => openScreen("opening")}
                                        testId="meditation-setup-row-opening"
                                        showDivider={false}
                                        value={openingLabel(opening)}
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}
                </main>
                {/* Full-width thumb target on mobile; from `sm` the CTA sits at its natural
                    width on the left, matching the app's left-aligned layout default. */}
                <div className="shrink-0 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-6 sm:pt-5">
                    <Button
                        variant="solidForest"
                        size="lg"
                        onClick={handleStart}
                        disabled={
                            (isPartMode && !selectedMeditation) ||
                            loading ||
                            (isPartMode && setupQuery.isError)
                        }
                        data-testid="meditation-setup-start"
                        analyticsId="Start Part Meditation (Setup)"
                        className="w-full sm:w-auto sm:px-10"
                    >
                        <Play className="size-5 fill-current" />
                        Start meditation
                    </Button>
                </div>
            </div>
        );
    }

    const overlay = (
        <div
            className="fixed inset-0 z-50 flex flex-col bg-stone-50"
            data-testid="meditation-setup-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="meditation-setup-title"
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 z-20 flex size-11 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 sm:right-6 sm:top-6"
            >
                <X className="size-6" />
            </button>
            {/* AIDEV-NOTE: The setup screen keeps a reading-width column; the picker screens
                manage their own (wider) width so their cards can fan out into a grid — see
                PickerScreen's WIDTH_CLASS. Capping everything at 28rem was what left the
                pickers as a phone-width column stranded in the middle of a desktop monitor. */}
            <div
                className={cn(
                    "mx-auto flex h-full min-h-0 w-full flex-col",
                    screen === "setup" ? "max-w-[34rem]" : "max-w-none",
                )}
            >
                <div key={screen} className={transitionClass}>
                    {content}
                </div>
            </div>
        </div>
    );

    return createPortal(overlay, document.body);
}
